import { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";

const Section = ({ num, title, children }) => (
  <section className="space-y-3" data-testid={`terms-section-${num}`}>
    <h2 className="text-lg sm:text-xl font-bold text-white">
      <span className="text-[#00cccc] font-mono text-sm mr-2">{num}.</span>{title}
    </h2>
    <div className="text-sm sm:text-[15px] text-zinc-400 leading-relaxed space-y-3">{children}</div>
  </section>
);

const Ul = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export default function TermsConditions() {
  useEffect(() => {
    document.title = "Terms & Conditions — Biker Boulevard";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="terms-page" className="relative bg-[#0D0F12] text-white min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,204,204,0.06), transparent 50%), #0D0F12" }} />
      </div>
      <Navbar />
      <div className="pt-28 pb-24 max-w-3xl mx-auto px-5 md:px-8">
        <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Legal</span>
        <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight" data-testid="terms-title">Terms &amp; Conditions</h1>
        <p className="mt-2 text-xs font-mono text-zinc-500">Effective Date: 10 October 2026</p>

        <div className="mt-8 space-y-4 text-sm sm:text-[15px] text-zinc-400 leading-relaxed">
          <p>
            Welcome to Biker Boulevard (&ldquo;BB&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;). These Terms &amp; Conditions govern your access to and use of our website, motorcycle marketplace, valuation tools, and related services.
          </p>
          <p>
            By accessing or using Biker Boulevard, you agree to these Terms. If you do not agree, please do not use the platform.
          </p>
        </div>

        <div className="mt-10 space-y-8">
          <Section num="1" title="About Biker Boulevard">
            <p>
              Biker Boulevard provides an online platform where users can discover, list, and enquire about pre-owned motorcycles. Our services may include motorcycle listings, user accounts, vehicle valuation estimates, and tools that facilitate communication between buyers and sellers.
            </p>
            <p>
              Unless expressly stated otherwise for a specific transaction, a listing on our marketplace is an offer published by the user who submitted it. A listing does not mean that Biker Boulevard owns, has inspected, certifies, or guarantees the motorcycle.
            </p>
          </Section>

          <Section num="2" title="User Accounts and Google Sign-In">
            <p>Users may be required to sign in using Google or another authentication method made available on the platform.</p>
            <p>You agree to:</p>
            <Ul items={[
              "Provide accurate and up-to-date information.",
              "Use an account that you are authorized to access.",
              "Keep your account and access credentials secure.",
              "Notify us if you suspect unauthorized use of your account.",
              "Avoid creating accounts for fraudulent, misleading, or unlawful purposes.",
            ]} />
            <p>You are responsible for activity conducted through your account, subject to applicable law.</p>
          </Section>

          <Section num="3" title="Listing a Motorcycle">
            <p>When submitting a motorcycle listing, you agree that:</p>
            <Ul items={[
              "You are the owner of the motorcycle or are authorized to list it.",
              "The information you provide is accurate, complete, and not misleading.",
              "You have the right to upload the photographs and other content submitted.",
              "You will disclose material facts about the motorcycle, including known damage, relevant ownership issues, loan status, or other significant issues where applicable.",
              "You will promptly correct or remove information that becomes inaccurate.",
              "You will not list stolen vehicles, vehicles you are not authorized to sell, or vehicles in violation of applicable law.",
            ]} />
            <p>
              Biker Boulevard may reject, restrict, edit for formatting, suspend, or remove listings that violate these Terms or create a reasonable concern about fraud, safety, legality, or platform integrity.
            </p>
          </Section>

          <Section num="4" title="Motorcycle Valuation Estimates">
            <p>
              Biker Boulevard may provide estimated buying prices, selling prices, or other valuation guidance through its Smart Valuation Engine.
            </p>
            <p>
              These estimates are informational and are generated using available vehicle details, condition inputs, market information, and other relevant factors. They are not guaranteed sale prices, purchase offers, or certified appraisals unless expressly stated otherwise.
            </p>
            <p>
              Actual motorcycle value may vary depending on physical inspection, documentation, service history, local demand, market conditions, and other factors.
            </p>
            <p>
              Buyers and sellers should independently verify a motorcycle&apos;s condition, ownership, documents, and fair market value before completing a transaction.
            </p>
          </Section>

          <Section num="5" title="Buyers and Sellers">
            <p>
              Unless expressly agreed otherwise in writing, buyers and sellers are responsible for negotiating and completing transactions between themselves.
            </p>
            <p>Before completing a purchase, buyers should independently verify:</p>
            <Ul items={[
              "The seller's identity and authority to sell.",
              "Registration and ownership documents.",
              "Outstanding loans, challans, or other relevant liabilities.",
              "Vehicle condition, accident history, and service records where available.",
              "Applicable transfer requirements and legal compliance.",
            ]} />
            <p>Sellers are responsible for providing truthful information and completing the required transfer and documentation processes.</p>
            <p>
              Users should exercise reasonable caution when meeting other users, inspecting motorcycles, sharing contact information, or making payments.
            </p>
          </Section>

          <Section num="6" title="Biker Boulevard's Role">
            <p>
              Unless a particular service or written agreement states otherwise, Biker Boulevard acts as a marketplace and technology service provider that facilitates discovery and communication.
            </p>
            <p>
              We do not automatically become a party to transactions between independent users merely because a listing appears on our website.
            </p>
            <p>
              We do not guarantee the accuracy of every user-submitted listing, the identity or conduct of every user, the condition or legal status of every motorcycle, or that a transaction will be completed.
            </p>
            <p>
              Where Biker Boulevard directly buys, sells, inspects, refurbishes, or otherwise provides a service in its own capacity, the terms applicable to that specific service or transaction will also apply.
            </p>
          </Section>

          <Section num="7" title="Fees and Payments">
            <p>
              Any listing fees, subscription charges, commissions, deposits, or other charges introduced by Biker Boulevard will be disclosed to users before they become payable.
            </p>
            <p>
              Unless expressly stated otherwise, users should not assume that a listing or valuation estimate constitutes a paid reservation, purchase commitment, or guarantee of a transaction.
            </p>
            <p>
              Payments made directly between users are their responsibility unless the platform expressly provides and governs the relevant payment service.
            </p>
          </Section>

          <Section num="8" title="Prohibited Activities">
            <p>You must not:</p>
            <Ul items={[
              "Publish false, misleading, fraudulent, or unauthorized listings.",
              "Misrepresent a motorcycle's ownership, condition, mileage, or documentation.",
              "Upload unlawful, infringing, abusive, or harmful content.",
              "Use the platform to scam, harass, threaten, or impersonate others.",
              "Attempt unauthorized access to accounts, databases, or platform systems.",
              "Scrape, copy, or commercially exploit platform content without authorization.",
              "Interfere with the platform's security or normal operation.",
              "Use the platform for any unlawful purpose.",
            ]} />
            <p>
              We may restrict or suspend access where we reasonably believe these Terms have been violated, subject to applicable law.
            </p>
          </Section>

          <Section num="9" title="User Content and Photographs">
            <p>You retain ownership of the content you submit, subject to rights belonging to other parties.</p>
            <p>
              By submitting listing photographs, descriptions, and related content, you grant Biker Boulevard a non-exclusive, royalty-free license to host, store, reproduce, display, and format that content as reasonably necessary to operate, promote, and improve the relevant listing and platform services.
            </p>
            <p>
              This license continues for as long as reasonably necessary to provide the service, maintain records, resolve disputes, or meet legal obligations. You confirm that you have the necessary rights to submit the content.
            </p>
          </Section>

          <Section num="10" title="Privacy">
            <p>Our collection and use of personal information are governed by our Privacy Policy, which forms part of these Terms.</p>
            <p>
              We treat personal information as confidential and use it in accordance with our Privacy Policy and applicable law. Users should understand that information included in public motorcycle listings may be visible to other users or the public.
            </p>
          </Section>

          <Section num="11" title="Intellectual Property">
            <p>
              The Biker Boulevard name, branding, website design, original content, software, and platform features are owned by or licensed to Biker Boulevard, except where otherwise indicated.
            </p>
            <p>
              You may use the platform for its intended purposes. You may not copy, reproduce, distribute, reverse engineer, or commercially exploit protected platform materials without prior authorization, except where permitted by law.
            </p>
          </Section>

          <Section num="12" title="Availability and Disclaimer">
            <p>
              We aim to keep the platform available and functioning properly, but we do not guarantee uninterrupted, error-free, or continuous availability.
            </p>
            <p>
              To the extent permitted by applicable law, the platform and its informational tools are provided on an &ldquo;as available&rdquo; basis. Nothing in these Terms excludes a right, remedy, or liability that cannot lawfully be excluded.
            </p>
          </Section>

          <Section num="13" title="Limitation of Liability">
            <p>
              To the extent permitted by applicable law, Biker Boulevard will not be liable for indirect or consequential losses arising from user-to-user transactions, inaccurate user-submitted information, or temporary platform interruptions.
            </p>
            <p>
              Nothing in these Terms limits liability for fraud, wilful misconduct, or any other liability that cannot legally be limited or excluded.
            </p>
            <p>
              These Terms do not remove any mandatory rights or remedies available to consumers under applicable Indian law.
            </p>
          </Section>

          <Section num="14" title="Suspension and Termination">
            <p>
              You may stop using the platform at any time. You may request account deletion or removal of your listing, subject to verification, technical requirements, ongoing transactions, and applicable record-retention obligations.
            </p>
            <p>
              We may suspend or terminate access for violations of these Terms, suspected fraud, security concerns, legal requirements, or misuse of the platform.
            </p>
          </Section>

          <Section num="15" title="Changes to These Terms">
            <p>
              We may revise these Terms as our business and services evolve. Updated Terms will be published on our website with a revised effective date where appropriate. Continued use after an update takes effect will be subject to applicable law and any consent requirements.
            </p>
          </Section>

          <Section num="16" title="Governing Law and Disputes">
            <p>These Terms are governed by the laws of India.</p>
            <p>
              Disputes will be subject to the jurisdiction of the competent courts in Lucknow, Uttar Pradesh, to the extent legally permissible. Nothing in this clause limits any mandatory consumer rights or jurisdictional protections available under applicable law.
            </p>
          </Section>

          <Section num="17" title="Contact Us">
            <p>For questions about these Terms or our platform, contact:</p>
            <div className="mt-2 p-4 rounded-xl glass" data-testid="terms-contact">
              <p className="text-white font-semibold">Biker Boulevard</p>
              <p>Email: <a href="mailto:reachbikerblvd@gmail.com" className="text-[#00cccc] hover:underline">reachbikerblvd@gmail.com</a></p>
              <p>Location: Lucknow, Uttar Pradesh, India</p>
            </div>
          </Section>
        </div>
      </div>
      <Footer />
    </main>
  );
}
